import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EMBED_TOKEN, parseEmbedParams, resolveEmbed } from './registry';

/**
 * ArticleEmbedHost — renders sanitised article HTML and swaps embed tokens for live components.
 *
 * ─── ⭐ WHY PORTALS AND NOT STRING SURGERY ──────────────────────────────────────────────────
 *
 * The obvious approach is to split the HTML string on the token and render the pieces around the
 * component. It is also wrong: a token sits inside a paragraph, so splitting yields `<p>` and
 * `</p>` as separate fragments and every consumer of that HTML then has unbalanced markup. React
 * would sanitise the halves independently and the article's structure would quietly collapse.
 *
 * So the HTML is injected ONCE, intact, and then the DOM is walked for tokens. Each token's text
 * node is replaced with an empty mount point, and the component is portalled into it. The article
 * markup is never cut.
 *
 * ⚠️ The walk runs after every html change and cleans up its own mount points, because articles
 * are fetched asynchronously and a re-fetch must not leave a previous render's nodes behind.
 */
const ArticleEmbedHost = ({ html, className }) => {
  const containerRef = useRef(null);
  const [mounts, setMounts] = useState([]);

  // Does this body contain any token at all? If not, skip the walk entirely — most articles are
  // ordinary prose and should pay nothing for this feature.
  const hasToken = useMemo(() => {
    if (!html) return false;
    EMBED_TOKEN.lastIndex = 0;
    return EMBED_TOKEN.test(html);
  }, [html]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root || !hasToken) { setMounts([]); return undefined; }

    const found = [];
    // A TreeWalker rather than innerHTML surgery: it reaches the text nodes without touching the
    // markup around them.
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const targets = [];
    let node = walker.nextNode();
    while (node) {
      EMBED_TOKEN.lastIndex = 0;
      if (EMBED_TOKEN.test(node.nodeValue || '')) targets.push(node);
      node = walker.nextNode();
    }

    targets.forEach((textNode, i) => {
      EMBED_TOKEN.lastIndex = 0;
      const match = EMBED_TOKEN.exec(textNode.nodeValue);
      if (!match) return;
      const [, name, rawParams] = match;
      const entry = resolveEmbed(name);

      const mountEl = document.createElement('div');
      mountEl.className = 'aai-embed-mount';

      // Replace the token's own paragraph when that paragraph holds nothing else — otherwise an
      // empty <p> is left sitting above the embed, which reads as a layout bug.
      const parent = textNode.parentElement;
      const parentIsLone = parent
        && parent !== root
        && (parent.textContent || '').trim() === match[0].trim();
      if (parentIsLone) parent.replaceWith(mountEl);
      else textNode.replaceWith(mountEl);

      found.push({
        key: `${name}-${i}`,
        el: mountEl,
        entry,
        name,
        params: parseEmbedParams(rawParams)
      });
    });

    setMounts(found);
    return () => { found.forEach((m) => m.el.remove()); };
  }, [html, hasToken]);

  return (
    <div className={className}>
      <div ref={containerRef} dangerouslySetInnerHTML={{ __html: html || '' }} />
      {mounts.map(({ key, el, entry, name, params }) => {
        if (!entry) {
          // ⚠️ Say so, visibly. A typo'd embed name that renders nothing produces an article with
          // a hole in it that nobody notices until a reader asks where the diagram went.
          return createPortal(
            <div className="aai-embed-missing" key={key}>
              Unknown embed: <code>{name}</code>
            </div>,
            el
          );
        }
        const Component = entry.component;
        return createPortal(<Component {...params} key={key} />, el);
      })}
    </div>
  );
};

export default ArticleEmbedHost;
